/**
 * Générateur — Automatisme : reconnaître et citer sur une configuration
 * géométrique des angles : plein, plat, nul, droit ; opposés par le sommet,
 * adjacents, supplémentaires, aigus, obtus.
 * Attendu ID : 1072 | Niveau : 5ème | Domaine : Angles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Quelle nature ?                          (QCM sur secteur colorié ; QCM « cite un angle… »)
 *   - Opposés, adjacents, supplémentaires ?    (V/F sur la configuration ; QCM « l'angle opposé à… »)
 *   - La mesure qui manque                     (saisie : supplémentaire, opposé, tour complet)
 *   - Angles : vrai ou faux ?                  (V/F pool du modèle + deux lignes numériques)
 *
 * ── Plan de cartes validé (2026-09-03) ─────────────────────────────────────
 * Source : Automatisme n°34 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : tout le lexique est INTRODUIT en 6e (liste identique à l'attendu),
 * basculé en automatisme en 5e ; le CM2 n'apporte que l'angle droit et la
 * comparaison aigu / obtus à l'équerre.
 * P2 : sens installés — nature par comparaison à l'angle droit, valeurs repères
 * 0 / 90 / 180 / 360, relations entre deux angles. Distracteurs sur les
 * confusions documentées : adjacents ⇄ supplémentaires, opposés crus
 * supplémentaires, obtus cru « plus de 180° », plat ⇄ plein.
 * P3 : mesures ENTIÈRES ; une soustraction à 180 ou à 360 au plus.
 * P5 : deux droites sécantes en O (+ une demi-droite), secteurs colorés ;
 * petit carré pour l'angle droit, alignement pour l'angle plat.
 * P6/P10 : pas de rapporteur, on ne mesure rien ; aigu / obtus se LISENT sur
 * des angles FRANCS (25° à 70°, 110° à 155°) ; « supplémentaires » se justifie
 * par l'angle plat, jamais par la mesure.
 * P7 : exclus (fiche) — complémentaires, alternes-internes, correspondants.
 * P8 : exigibles depuis la 6e — tout le lexique, [OA), l'angle AOB.
 * P11 : adjacents n'implique pas supplémentaires, et réciproquement ; opposés
 * par le sommet ⟹ égaux, pas supplémentaires.
 * P12 : rémanence forte 6e → 5e, pas d'item de réactivation.
 * INVARIANTS :
 *   - la configuration est CALCULÉE (angles des demi-droites en degrés entiers)
 *     et toutes les relations (adjacents, supplémentaires, opposés) sont
 *     RECALCULÉES dessus, jamais écrites à la main ;
 *   - l'angle droit n'apparaît que codé (petit carré) ; l'angle plat n'est
 *     jamais demandé sans alignement dessiné ; jamais d'angle « presque droit »
 *     (secteurs entre 25° et 70° ou entre 110° et 155°, hors carte droit) ;
 *   - data-rays (demi-droites) et data-angle (secteur colorié) dans le SVG pour
 *     les solveurs ; réponses de saisie ENTIÈRES ; pas de calculatrice ;
 *   - V/F : fenêtre stricte des 4 dernières lignes fixes servies.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1072;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'Quelle nature ?';
const L_RELATION = 'Opposés, adjacents, supplémentaires ?';
const L_MESURE = 'La mesure qui manque';
const L_VF = 'Angles : vrai ou faux ?';

// ─── Configuration : demi-droites issues de O, angles en degrés ────────────
// Une configuration = { rayons: { A: θ, B: θ+180, C: …, D: …, E: … } } (θ en
// degrés, sens trigonométrique). Deux droites (AB) et (CD) sécantes en O,
// éventuellement une demi-droite [OE) à l'intérieur d'un secteur.

const mod360 = (x) => ((x % 360) + 360) % 360;

/** Mesure de l'angle XOY (entre 0 et 180, le petit angle) — sauf si on demande le grand. */
const mesureAngle = (rayons, X, Y) => {
    const d = Math.abs(mod360(rayons[X] - rayons[Y]));
    return d > 180 ? 360 - d : d;
};

/** Deux angles adjacents : même sommet (toujours O), un côté commun, de part et d'autre de ce côté. */
const sontAdjacents = (rayons, [p, q], [r, s]) => {
    const communs = [p, q].filter((x) => x === r || x === s);
    if (communs.length !== 1) return false;
    const c = communs[0];
    const autre1 = p === c ? q : p; const autre2 = r === c ? s : r;
    if (autre1 === autre2) return false;
    // de part et d'autre : les deux angles ne se recouvrent pas
    const a1 = mod360(rayons[autre1] - rayons[c]); const a2 = mod360(rayons[autre2] - rayons[c]);
    const cote1 = a1 <= 180 ? 1 : -1; const cote2 = a2 <= 180 ? 1 : -1;
    return cote1 !== cote2;
};

const sontSupplementaires = (rayons, a1, a2) => mesureAngle(rayons, ...a1) + mesureAngle(rayons, ...a2) === 180;

/** Opposés par le sommet : les côtés de l'un sont les prolongements des côtés de l'autre. */
const sontOpposes = (rayons, [p, q], [r, s]) => {
    const oppose = (x, y) => mod360(rayons[x] - rayons[y]) === 180;
    return (oppose(p, r) && oppose(q, s)) || (oppose(p, s) && oppose(q, r));
};

const NATURE_PAR_MESURE = (m) => {
    if (m === 0) return 'nul';
    if (m < 90) return 'aigu';
    if (m === 90) return 'droit';
    if (m < 180) return 'obtus';
    if (m === 180) return 'plat';
    return 'plein';
};

const NOMS_NATURES = { nul: 'un angle nul', aigu: 'un angle aigu', droit: 'un angle droit', obtus: 'un angle obtus', plat: 'un angle plat', plein: 'un angle plein' };

/** Natures voisines (celles qu'on confond), toujours servies en leurres. */
const VOISINES_NATURE = {
    aigu: ['droit', 'obtus'], obtus: ['droit', 'plat'], droit: ['aigu', 'obtus'],
    plat: ['obtus', 'plein'], plein: ['plat', 'obtus'], nul: ['aigu', 'droit'],
};

/** Tire un angle aigu ou obtus FRANC (jamais presque droit, jamais presque plat). */
const tirerAigu = () => randInt(25, 70);
const tirerObtus = () => randInt(110, 155);

/**
 * Configuration standard : (AB) et (CD) sécantes en O, angle AOD = phi
 * (aigu ou obtus franc), rotation aléatoire ; E optionnel dans le secteur AOD.
 */
/** Un angle est FRANC s'il se lit sans hésiter : aigu net ou obtus net. */
const estFranc = (m) => (m >= 25 && m <= 70) || (m >= 110 && m <= 155);

const construireConfiguration = ({ phi = null, avecE = false, droit = false } = {}) => {
    const theta = randInt(0, 359);
    let ouverture = droit ? 90 : (phi ?? (Math.random() < 0.5 ? tirerAigu() : tirerObtus()));
    let positionsE = [];
    if (avecE) {
        // E à l'intérieur de AOD tel que AOE et EOD soient FRANCS (revue 2026-09-03, A7) ;
        // EOB = 180 − AOE et EOC = 180 − EOD le sont alors aussi. Si l'ouverture ne le
        // permet pas (aigu < 50°), on retire une ouverture compatible.
        const candidats = (ouv) => { const r = []; for (let a = 1; a < ouv; a++) if (estFranc(a) && estFranc(ouv - a)) r.push(a); return r; };
        positionsE = candidats(ouverture);
        for (let essai = 0; essai < 50 && positionsE.length === 0; essai++) {
            ouverture = Math.random() < 0.5 ? tirerAigu() : tirerObtus();
            positionsE = candidats(ouverture);
        }
        if (positionsE.length === 0) throw new Error('gen_1072 : aucune position franche pour E');
    }
    const rayons = { A: mod360(theta), D: mod360(theta + ouverture), B: mod360(theta + 180), C: mod360(theta + ouverture + 180) };
    if (avecE) rayons.E = mod360(theta + randomChoice(positionsE));
    return { rayons, ouverture, theta };
};

// ─── Rendu SVG ─────────────────────────────────────────────────────────────

const W = 320; const H = 240; const O = [160, 124]; const RAYON = 88; // lettres à RAYON + 14 : toujours dans le cadre
const f1 = (v) => v.toFixed(1);
const pointSur = (deg, r) => [O[0] + r * Math.cos((deg * Math.PI) / 180), O[1] - r * Math.sin((deg * Math.PI) / 180)];

/** Arc (rempli ou non) de a1 vers a2 dans le sens trigonométrique, rayon r. */
const svgArc = (a1, a2, r, attrs) => {
    const sweep = mod360(a2 - a1);
    if (sweep === 0) return '';
    const p1 = pointSur(a1, r); const p2 = pointSur(a2, r);
    const large = sweep > 180 ? 1 : 0;
    // sens trigonométrique = sens anti-horaire à l'écran (y inversé) → sweep-flag 0
    return `<path d="M ${f1(O[0])} ${f1(O[1])} L ${f1(p1[0])} ${f1(p1[1])} A ${r} ${r} 0 ${large} 0 ${f1(p2[0])} ${f1(p2[1])} Z" ${attrs}/>`;
};

const svgArcOuvert = (a1, a2, r, attrs) => {
    const sweep = mod360(a2 - a1);
    if (sweep === 0) return '';
    const p1 = pointSur(a1, r); const p2 = pointSur(a2, r);
    const large = sweep > 180 ? 1 : 0;
    return `<path d="M ${f1(p1[0])} ${f1(p1[1])} A ${r} ${r} 0 ${large} 0 ${f1(p2[0])} ${f1(p2[1])}" fill="none" ${attrs}/>`;
};

const svgCarre = (a1, a2) => {
    const k = 12;
    const p1 = pointSur(a1, k); const p3 = pointSur(a2, k);
    const p2 = [p1[0] + p3[0] - O[0], p1[1] + p3[1] - O[1]];
    return `<polyline points="${f1(p1[0])},${f1(p1[1])} ${f1(p2[0])},${f1(p2[1])} ${f1(p3[0])},${f1(p3[1])}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre"/>`;
};

/** Secteur (X, Y) parcouru dans le sens trigonométrique de X vers Y : le PETIT angle si ≤ 180. */
const secteurTrigo = (rayons, X, Y) => (mod360(rayons[Y] - rayons[X]) <= 180 ? [rayons[X], rayons[Y]] : [rayons[Y], rayons[X]]);

/**
 * svgConfiguration(config, options)
 *  options.colorie : [X, Y] → secteur XOY colorié (petit angle) ; 'plein' → tour complet
 *  options.droit   : [X, Y] → petit carré
 *  options.valeurs : { 'XY': '126°' } → mesure écrite dans le secteur XOY
 *  options.arcs    : [[X, Y], …] → arcs fins (angles cités)
 */
const svgConfiguration = (config, options = {}) => {
    const { rayons } = config;
    let s = '';
    // secteur colorié
    if (options.colorie === 'plein') {
        s += `<circle cx="${f1(O[0])}" cy="${f1(O[1])}" r="26" fill="#fde68a" fill-opacity="0.7" stroke="#B45309" stroke-width="1.6"/>`
            + `<path d="M ${f1(O[0] + 26)} ${f1(O[1])} l -6 -6 M ${f1(O[0] + 26)} ${f1(O[1])} l 6 -6" stroke="#B45309" stroke-width="1.6" fill="none"/>`;
    } else if (options.colorie) {
        const [a1, a2] = secteurTrigo(rayons, options.colorie[0], options.colorie[1]);
        s += svgArc(a1, a2, 30, 'fill="#fde68a" fill-opacity="0.75" stroke="#B45309" stroke-width="1.6"');
    }
    (options.arcs || []).forEach(([X, Y], i) => {
        const [a1, a2] = secteurTrigo(rayons, X, Y);
        s += svgArcOuvert(a1, a2, 22 + i * 6, 'stroke="#B45309" stroke-width="1.5"');
    });
    // demi-droites : les droites (AB) et (CD) sont tracées pleines, [OE) aussi
    Object.entries(rayons).forEach(([nom, deg]) => {
        const P = pointSur(deg, RAYON);
        s += `<line x1="${f1(O[0])}" y1="${f1(O[1])}" x2="${f1(P[0])}" y2="${f1(P[1])}" stroke="#1E3A8A" stroke-width="2.2"/>`;
        const L = pointSur(deg, RAYON + 14);
        s += `<text x="${f1(L[0])}" y="${f1(L[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
    });
    if (options.droit) {
        const [a1, a2] = secteurTrigo(rayons, options.droit[0], options.droit[1]);
        s += svgCarre(a1, a2);
    }
    // valeurs écrites : sur la bissectrice du secteur, recul adaptatif (étroit → plus loin)
    Object.entries(options.valeurs || {}).forEach(([cle, txt]) => {
        const [a1, a2] = secteurTrigo(rayons, cle[0], cle[1]);
        const sweep = mod360(a2 - a1); const bis = a1 + sweep / 2;
        const recul = Math.max(44, 12 / Math.tan((Math.max(sweep, 8) * Math.PI) / 360) + 8);
        const P = pointSur(bis, Math.min(recul, 78));
        s += `<text x="${f1(P[0])}" y="${f1(P[1] + 4)}" font-size="12.5" font-weight="700" fill="#B45309" text-anchor="middle">${txt}</text>`;
    });
    const occupes = [];
    if (options.colorie && options.colorie !== 'plein') occupes.push(secteurTrigo(rayons, options.colorie[0], options.colorie[1]));
    if (options.droit) occupes.push(secteurTrigo(rayons, options.droit[0], options.droit[1]));
    const lO = positionEtiquetteO(rayons, occupes, options.colorie === 'plein' ? 36 : 16);
    s += `<circle cx="${f1(O[0])}" cy="${f1(O[1])}" r="2.6" fill="#1e293b"/>`
        + `<text x="${f1(lO[0])}" y="${f1(lO[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">O</text>`;
    const dataRays = Object.entries(rayons).map(([n, d]) => `${n}:${d}`).join('|');
    const dataAngle = options.colorie === 'plein' ? 'plein' : options.colorie ? options.colorie.join('') : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-rays="${dataRays}" data-angle="${dataAngle}" data-droit="${options.droit ? options.droit.join('') : ''}">${s}</svg>`;
};

/**
 * Position de l'étiquette « O » : sur la bissectrice du plus grand secteur libre
 * entre les demi-droites, en évitant le secteur colorié ou codé (retour prof 2026-09-03).
 */
const positionEtiquetteO = (rayons, secteursOccupes = [], dist = 16) => {
    const angles = Object.values(rayons).map(mod360).sort((x, y) => x - y);
    if (angles.length === 0) return pointSur(225, dist);
    const dansSecteur = (deg, [a1, a2]) => mod360(deg - a1) <= mod360(a2 - a1);
    let meilleur = null; let ecartMax = -1;
    for (let k = 0; k < angles.length; k++) {
        const suivant = k + 1 < angles.length ? angles[k + 1] : angles[0] + 360;
        const ecart = suivant - angles[k]; const bis = mod360(angles[k] + ecart / 2);
        if (secteursOccupes.some((sec) => dansSecteur(bis, sec))) continue;
        if (ecart > ecartMax) { ecartMax = ecart; meilleur = bis; }
    }
    if (meilleur === null) { meilleur = mod360(angles[0] + 180 / angles.length); ecartMax = 360 / angles.length; }
    // Recul adaptatif : au moins 9 px de chaque demi-droite bordant le secteur (plafond 40 px).
    const recul = Math.min(40, Math.max(dist, 9 / Math.sin((Math.max(ecartMax, 10) * Math.PI) / 360)));
    return pointSur(meilleur, recul);
};

// Nom d'angle avec le chapeau sur le sommet : AÔD (accent circonflexe combinant, demande prof 2026-09-04).
const CHAPEAU = '\u0302';
const nomAngle = (X, Y) => `${X}O${CHAPEAU}${Y}`;

// ─── Carte 1a : quelle nature ? (QCM sur secteur colorié) ──────────────────

let derniereSignatureNature1072 = null;

const genNatureQcm = () => {
    let payload = construireNatureQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureNature1072; t++) payload = construireNatureQcm();
    derniereSignatureNature1072 = payload._meta.params.signature;
    return payload;
};

const construireNatureQcm = () => {
    // Natures servies : aigu, obtus, droit (codé), plat (alignement), plein (tour complet).
    const cible = randomChoice(['aigu', 'obtus', 'droit', 'plat', 'aigu', 'obtus', 'plein']);
    let config; let colorie; let droit = null; let texte;
    if (cible === 'plein') {
        config = construireConfiguration({ avecE: false });
        colorie = 'plein';
        texte = '<p>Les droites (AB) et (CD) se coupent en O.</p><p>Quelle est la nature de l\'angle colorié, qui fait le tour complet autour de O ?</p>';
    } else if (cible === 'plat') {
        config = construireConfiguration({ avecE: Math.random() < 0.5 });
        const paire = randomChoice([['A', 'B'], ['C', 'D']]);
        colorie = paire;
        texte = `<p>Les droites (AB) et (CD) se coupent en O.</p><p>Quelle est la nature de l'angle ${nomAngle(...paire)} (colorié) ?</p>`;
    } else if (cible === 'droit') {
        config = construireConfiguration({ droit: true });
        colorie = randomChoice([['A', 'D'], ['D', 'B'], ['B', 'C'], ['C', 'A']]);
        droit = colorie;
        texte = `<p>Les droites (AB) et (CD) se coupent en O.</p><p>Quelle est la nature de l'angle ${nomAngle(...colorie)} (colorié) ?</p>`;
    } else {
        config = construireConfiguration({ phi: cible === 'aigu' ? tirerAigu() : tirerObtus() });
        // AOD et BOC valent phi ; DOB et COA valent 180 − phi
        const memeNature = randomChoice([['A', 'D'], ['B', 'C']]);
        const autre = randomChoice([['D', 'B'], ['C', 'A']]);
        colorie = mesureAngle(config.rayons, ...memeNature) < 90 === (cible === 'aigu') ? memeNature : autre;
        texte = `<p>Les droites (AB) et (CD) se coupent en O.</p><p>Quelle est la nature de l'angle ${nomAngle(...colorie)} (colorié) ?</p>`;
    }
    // Vérification : la nature de l'angle colorié, recalculée, est bien la cible.
    const mesure = colorie === 'plein' ? 360 : mesureAngle(config.rayons, ...colorie);
    if (NATURE_PAR_MESURE(mesure) !== cible) throw new Error(`gen_1072 : nature ${cible} contredite (${mesure}°)`);
    const correct = NOMS_NATURES[cible];
    // Leurres : les deux natures VOISINES de la cible, plus une tirée au hasard (revue 2026-09-03, A3).
    const voisines = VOISINES_NATURE[cible];
    const troisieme = randomChoice(Object.keys(NOMS_NATURES).filter((n) => n !== cible && !voisines.includes(n)));
    const autres = shuffle([...voisines, troisieme]).map((n) => NOMS_NATURES[n]);
    const svg = svgConfiguration(config, { colorie, droit });
    const payload = makeQcm([correct, ...autres], correct, texte, {
        ...opts(L_NATURE), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload._meta.params = { cible, mesure, signature: `${cible}-${colorie === 'plein' ? 'plein' : colorie.join('')}-${config.theta}` };
    return payload;
};

// ─── Carte 1b : cite un angle… (QCM) ───────────────────────────────────────

/** Tous les angles nommables XOY (X ≠ Y) de la configuration, avec leur mesure (petit angle). */
const tousLesAngles = (rayons) => {
    const noms = Object.keys(rayons); const res = [];
    for (let i = 0; i < noms.length; i++) for (let j = i + 1; j < noms.length; j++) {
        res.push({ X: noms[i], Y: noms[j], mesure: mesureAngle(rayons, noms[i], noms[j]) });
    }
    return res;
};

let derniereSignatureCite1072 = null;

const genCiteQcm = () => {
    let payload = construireCiteQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCite1072; t++) payload = construireCiteQcm();
    derniereSignatureCite1072 = payload._meta.params.signature;
    return payload;
};

const construireCiteQcm = () => {
    const config = construireConfiguration({ avecE: true });
    const { rayons } = config;
    const angles = tousLesAngles(rayons);
    const nomDe = (a) => nomAngle(a.X, a.Y);
    const sousType = randomChoice(['plat', 'aigu_E', 'obtus', 'aigu']);
    let bonnes; let leurres; let texte;
    if (sousType === 'plat') {
        bonnes = angles.filter((a) => a.mesure === 180);
        leurres = angles.filter((a) => a.mesure !== 180);
        texte = '<p>Les droites (AB) et (CD) se coupent en O.</p><p>Cite un angle plat.</p>';
    } else if (sousType === 'aigu_E') {
        bonnes = angles.filter((a) => (a.X === 'E' || a.Y === 'E') && a.mesure < 90);
        leurres = angles.filter((a) => !((a.X === 'E' || a.Y === 'E') && a.mesure < 90));
        texte = '<p>Les droites (AB) et (CD) se coupent en O.</p><p>Cite un angle aigu ayant [OE) pour côté.</p>';
    } else if (sousType === 'obtus') {
        bonnes = angles.filter((a) => a.mesure > 90 && a.mesure < 180);
        leurres = angles.filter((a) => !(a.mesure > 90 && a.mesure < 180));
        texte = '<p>Les droites (AB) et (CD) se coupent en O.</p><p>Cite un angle obtus.</p>';
    } else {
        bonnes = angles.filter((a) => a.mesure < 90);
        leurres = angles.filter((a) => a.mesure >= 90);
        texte = '<p>Les droites (AB) et (CD) se coupent en O.</p><p>Cite un angle aigu.</p>';
    }
    if (bonnes.length === 0 || leurres.length < 3) throw new Error(`gen_1072 : cite ${sousType} — pas assez d'angles`);
    const bonne = randomChoice(bonnes);
    let pieges;
    if (sousType === 'aigu_E') {
        // Au moins un leurre OBTUS ayant [OE) pour côté (revue 2026-09-03, A2) : la lettre E ne suffit pas.
        const obtusAvecE = leurres.filter((a) => (a.X === 'E' || a.Y === 'E') && a.mesure > 90 && a.mesure < 180);
        if (obtusAvecE.length === 0) throw new Error('gen_1072 : aucun obtus avec E');
        const force = randomChoice(obtusAvecE);
        pieges = [force, ...shuffle(leurres.filter((a) => a !== force)).slice(0, 2)];
    } else {
        pieges = shuffle(leurres).slice(0, 3);
    }
    // Garde : un seul angle recevable parmi les quatre options.
    const options = [bonne, ...pieges];
    const recevables = options.filter((a) => bonnes.includes(a));
    if (recevables.length !== 1) throw new Error('gen_1072 : plusieurs options recevables');
    const svg = svgConfiguration(config, {});
    const payload = makeQcm(options.map(nomDe), nomDe(bonne), texte, {
        ...opts(L_NATURE), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, bonne: nomDe(bonne), signature: `${sousType}-${nomDe(bonne)}-${pieges.map(nomDe).sort().join('')}` };
    return payload;
};

// ─── Carte 2a : V/F sur la configuration ───────────────────────────────────

/** Gabarits de lignes ; chaque ligne est RECALCULÉE sur la configuration servie. */
const GABARITS_RELATION = [
    { cle: 'oppose', paires: [['A', 'D'], ['B', 'C']], texte: (a, b) => `Les angles ${a} et ${b} sont opposés par le sommet.`, verite: (r, p, q) => sontOpposes(r, p, q) },
    { cle: 'oppose', paires: [['D', 'B'], ['C', 'A']], texte: (a, b) => `Les angles ${a} et ${b} sont opposés par le sommet.`, verite: (r, p, q) => sontOpposes(r, p, q) },
    { cle: 'oppose_faux', paires: [['A', 'D'], ['D', 'B']], texte: (a, b) => `Les angles ${a} et ${b} sont opposés par le sommet.`, verite: (r, p, q) => sontOpposes(r, p, q) },
    { cle: 'oppose_faux2', paires: [['A', 'C'], ['C', 'B']], texte: (a, b) => `Les angles ${a} et ${b} sont opposés par le sommet.`, verite: (r, p, q) => sontOpposes(r, p, q) },
    { cle: 'adj', paires: [['A', 'D'], ['D', 'B']], texte: (a, b) => `Les angles ${a} et ${b} sont adjacents.`, verite: (r, p, q) => sontAdjacents(r, p, q) },
    { cle: 'adj_E', paires: [['A', 'E'], ['E', 'D']], texte: (a, b) => `Les angles ${a} et ${b} sont adjacents.`, verite: (r, p, q) => sontAdjacents(r, p, q) },
    { cle: 'adj_faux', paires: [['A', 'D'], ['B', 'C']], texte: (a, b) => `Les angles ${a} et ${b} sont adjacents.`, verite: (r, p, q) => sontAdjacents(r, p, q) },
    { cle: 'adj_faux2', paires: [['A', 'E'], ['A', 'D']], texte: (a, b) => `Les angles ${a} et ${b} sont adjacents.`, verite: (r, p, q) => sontAdjacents(r, p, q) },
    { cle: 'supp', paires: [['A', 'D'], ['D', 'B']], texte: (a, b) => `Les angles ${a} et ${b} sont supplémentaires.`, verite: (r, p, q) => sontSupplementaires(r, p, q) },
    { cle: 'supp_E', paires: [['A', 'E'], ['E', 'B']], texte: (a, b) => `Les angles ${a} et ${b} sont supplémentaires.`, verite: (r, p, q) => sontSupplementaires(r, p, q) },
    { cle: 'supp_faux', paires: [['A', 'E'], ['E', 'D']], texte: (a, b) => `Les angles ${a} et ${b} sont supplémentaires.`, verite: (r, p, q) => sontSupplementaires(r, p, q) },
    { cle: 'supp_faux2', paires: [['A', 'D'], ['B', 'C']], texte: (a, b) => `Les angles ${a} et ${b} sont supplémentaires.`, verite: (r, p, q) => sontSupplementaires(r, p, q) },
];

let derniersGabaritsRelation1072 = [];
let dernieresLignesRelation1072 = []; // les 8 dernières lignes servies mot pour mot (deux tirages)

/**
 * Permutation des lettres (revue 2026-09-03, A8) : les gabarits sont écrits en
 * RÔLES canoniques (A, B sur une droite ; D = A + ouverture ; C opposé à D ; E
 * dans le secteur AOD). À chaque tirage, les rôles reçoivent des lettres réelles
 * (A ↔ B, C ↔ D, ou échange des deux droites) : 8 versions par gabarit, E tombe
 * dans l'un des quatre secteurs, l'élève relit la figure.
 */
const permuterLettres = (rayonsRoles) => {
    const echangerDroites = Math.random() < 0.5; const inverser1 = Math.random() < 0.5; const inverser2 = Math.random() < 0.5;
    let premiere = echangerDroites ? ['C', 'D'] : ['A', 'B']; let seconde = echangerDroites ? ['A', 'B'] : ['C', 'D'];
    if (inverser1) premiere = [premiere[1], premiere[0]];
    if (inverser2) seconde = [seconde[1], seconde[0]];
    // rôles A, B → première droite ; rôles D, C → seconde droite (D et C opposés)
    const carte = { A: premiere[0], B: premiere[1], D: seconde[0], C: seconde[1], E: 'E' };
    const rayons = {};
    Object.entries(rayonsRoles).forEach(([role, deg]) => { rayons[carte[role]] = deg; });
    return { carte, rayons };
};

const genRelationVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        // phi ≠ 90 : AOD et BOC (opposés, égaux) ne sont jamais supplémentaires par accident.
        const config = construireConfiguration({ avecE: true });
        const { carte, rayons } = permuterLettres(config.rayons);
        const dispo = GABARITS_RELATION.filter((g) => !derniersGabaritsRelation1072.includes(g.cle));
        const choisis = shuffle(dispo).slice(0, 4);
        // Au plus DEUX lignes sur une même paire d'angles (revue 2026-09-03, A8).
        const clePaire = (g) => g.paires.map((pq) => [...pq].sort().join('')).sort().join('-');
        const comptes = {};
        choisis.forEach((g) => { comptes[clePaire(g)] = (comptes[clePaire(g)] || 0) + 1; });
        if (Object.values(comptes).some((c) => c > 2)) continue;
        const lignes = choisis.map((g) => {
            const [p, q] = g.paires.map((pq) => pq.map((role) => carte[role]));
            return { l: g.texte(nomAngle(...p), nomAngle(...q)), v: g.verite(rayons, p, q), cle: g.cle };
        });
        const nbFaux = lignes.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        // Jamais une ligne servie mot pour mot dans les deux tirages précédents (lettres permutées comprises).
        if (lignes.some((it) => dernieresLignesRelation1072.includes(it.l))) continue;
        derniersGabaritsRelation1072 = [...derniersGabaritsRelation1072, ...choisis.map((g) => g.cle)].slice(-4);
        dernieresLignesRelation1072 = [...dernieresLignesRelation1072, ...lignes.map((it) => it.l)].slice(-8);
        const svg = svgConfiguration({ ...config, rayons }, {});
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Les droites (AB) et (CD) se coupent en O. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_RELATION), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { cles: choisis.map((g) => g.cle), carte, theta: config.theta };
        return payload;
    }
    throw new Error('gen_1072 : V/F relation sans mixité après 60 essais');
};

// ─── Carte 2b : l'angle opposé par le sommet (QCM) ─────────────────────────

let derniereSignatureOppose1072 = null;

const genOpposeQcm = () => {
    let payload = construireOpposeQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureOppose1072; t++) payload = construireOpposeQcm();
    derniereSignatureOppose1072 = payload._meta.params.signature;
    return payload;
};

const construireOpposeQcm = () => {
    // [OE) toujours tracée (revue 2026-09-03, A2) : il faut des angles SANS lettre commune avec le départ parmi les leurres.
    const config = construireConfiguration({ avecE: true });
    const { rayons } = config;
    const angles = tousLesAngles(rayons).filter((a) => a.mesure !== 180 && a.X !== 'E' && a.Y !== 'E');
    const depart = randomChoice(angles);
    const bonnes = angles.filter((a) => a !== depart && sontOpposes(rayons, [depart.X, depart.Y], [a.X, a.Y]));
    if (bonnes.length !== 1) throw new Error('gen_1072 : angle opposé introuvable ou multiple');
    const bonne = bonnes[0];
    const nomDe = (a) => nomAngle(a.X, a.Y);
    // Leurres filtrés par NOM (revue 2026-09-03, A3 : un filtre par identité laissait passer la bonne en double).
    const exclus = [nomDe(depart), nomDe(bonne)];
    const candidats = tousLesAngles(rayons).filter((a) => !exclus.includes(nomDe(a)));
    // Au moins un leurre sans lettre commune avec l'angle de départ (comme la bonne réponse).
    const sansCommune = candidats.filter((a) => a.X !== depart.X && a.X !== depart.Y && a.Y !== depart.X && a.Y !== depart.Y);
    if (sansCommune.length === 0) throw new Error('gen_1072 : aucun leurre sans lettre commune');
    const force = randomChoice(sansCommune);
    const leurres = [force, ...shuffle(candidats.filter((a) => a !== force)).slice(0, 2)];
    if (leurres.length !== 3 || new Set([...exclus, ...leurres.map(nomDe)]).size !== 5) throw new Error('gen_1072 : QCM opposé sans 4 options distinctes');
    const texte = `<p>Les droites (AB) et (CD) se coupent en O.</p><p>Cite l'angle opposé par le sommet à l'angle ${nomDe(depart)} (colorié).</p>`;
    const svg = svgConfiguration(config, { colorie: [depart.X, depart.Y] });
    const payload = makeQcm([nomDe(bonne), ...leurres.map(nomDe)], nomDe(bonne), texte, {
        ...opts(L_RELATION), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    payload._meta.params = { depart: nomDe(depart), bonne: nomDe(bonne), signature: `${nomDe(depart)}-${leurres.map(nomDe).sort().join('')}` };
    return payload;
};

// ─── Carte 3 : la mesure qui manque (saisie) ───────────────────────────────

let derniereSignatureMesure1072 = null;

const genMesureSaisie = () => {
    let payload = construireMesureSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureMesure1072; t++) payload = construireMesureSaisie();
    derniereSignatureMesure1072 = payload._meta.params.signature;
    return payload;
};

const construireMesureSaisie = () => {
    const sousType = randomChoice(['supplementaire', 'oppose', 'tour']);
    let texte; let reponse; let svg;
    if (sousType === 'tour') {
        // Trois demi-droites [OA), [OB), [OC) autour de O : les trois angles font 360°.
        // Mesures en multiples de 5 (revue 2026-09-03) : le complément à 360 reste du calcul mental.
        const a = 5 * randInt(14, 30); const b = 5 * randInt(14, 30); const c = 360 - a - b;
        if (c < 70 || c > 170) return construireMesureSaisie();
        const theta = randInt(0, 359);
        const config = { rayons: { A: mod360(theta), B: mod360(theta + a), C: mod360(theta + a + b) }, theta };
        const inconnu = randomChoice(['AB', 'BC', 'CA']);
        const valeurs = { AB: a, BC: b, CA: c };
        const donnes = Object.keys(valeurs).filter((k) => k !== inconnu);
        reponse = valeurs[inconnu];
        const etiquettes = {}; donnes.forEach((k) => { etiquettes[k] = `${valeurs[k]}°`; }); etiquettes[inconnu] = '?';
        svg = svgConfigurationTour(config, etiquettes);
        texte = `<p>Trois demi-droites [OA), [OB) et [OC) sont issues du point O. L'angle ${nomAngle(...donnes[0])} mesure ${valeurs[donnes[0]]}° et l'angle ${nomAngle(...donnes[1])} mesure ${valeurs[donnes[1]]}°.</p><p>Calcule la mesure de l'angle ${nomAngle(...inconnu)}.</p>`;
    } else {
        const phi = Math.random() < 0.5 ? tirerAigu() : tirerObtus();
        const config = construireConfiguration({ phi });
        const { rayons } = config;
        const donne = randomChoice([['A', 'D'], ['D', 'B'], ['B', 'C'], ['C', 'A']]);
        const mesureDonnee = mesureAngle(rayons, ...donne);
        let cherche;
        if (sousType === 'oppose') {
            cherche = tousLesAngles(rayons).find((a) => sontOpposes(rayons, donne, [a.X, a.Y]));
            cherche = [cherche.X, cherche.Y];
            reponse = mesureDonnee;
        } else {
            cherche = randomChoice(tousLesAngles(rayons).filter((a) => a.mesure !== 180 && sontAdjacents(rayons, donne, [a.X, a.Y]) && sontSupplementaires(rayons, donne, [a.X, a.Y])).map((a) => [a.X, a.Y]));
            reponse = 180 - mesureDonnee;
        }
        if (mesureAngle(rayons, ...cherche) !== reponse) throw new Error('gen_1072 : mesure cherchée incohérente');
        // Un arc pour l'angle donné et un pour l'angle cherché (retour prof 2026-09-03).
        svg = svgConfiguration(config, { valeurs: { [donne.join('')]: `${mesureDonnee}°`, [cherche.join('')]: '?' }, arcs: [donne, cherche] });
        texte = `<p>Les droites (AB) et (CD) se coupent en O. L'angle ${nomAngle(...donne)} mesure ${mesureDonnee}°.</p><p>Calcule la mesure de l'angle ${nomAngle(...cherche)}.</p>`;
    }
    if (!Number.isInteger(reponse) || reponse <= 0) throw new Error('gen_1072 : réponse non entière');
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_MESURE), suffixe: '°', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, reponse, signature: `${sousType}-${reponse}-${texte.slice(-30)}` };
    return payload;
};

/** Figure « tour complet » : trois demi-droites, les trois secteurs étiquetés. */
const svgConfigurationTour = (config, etiquettes) => {
    const { rayons } = config;
    let s = '';
    const secteurs = [['A', 'B'], ['B', 'C'], ['C', 'A']];
    secteurs.forEach(([X, Y], i) => {
        const a1 = rayons[X]; const a2 = rayons[Y]; const sweep = mod360(a2 - a1);
        s += svgArcOuvert(a1, a2, 22 + (i % 2) * 5, 'stroke="#B45309" stroke-width="1.5"');
        const bis = a1 + sweep / 2;
        const P = pointSur(bis, 46);
        s += `<text x="${f1(P[0])}" y="${f1(P[1] + 4)}" font-size="12.5" font-weight="700" fill="#B45309" text-anchor="middle">${etiquettes[X + Y]}</text>`;
    });
    Object.entries(rayons).forEach(([nom, deg]) => {
        const P = pointSur(deg, RAYON);
        s += `<line x1="${f1(O[0])}" y1="${f1(O[1])}" x2="${f1(P[0])}" y2="${f1(P[1])}" stroke="#1E3A8A" stroke-width="2.2"/>`;
        const L = pointSur(deg, RAYON + 14);
        s += `<text x="${f1(L[0])}" y="${f1(L[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
    });
    const lO = positionEtiquetteO(rayons, [], 16);
    s += `<circle cx="${f1(O[0])}" cy="${f1(O[1])}" r="2.6" fill="#1e293b"/>`
        + `<text x="${f1(lO[0])}" y="${f1(lO[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">O</text>`;
    const dataRays = Object.entries(rayons).map(([n, d]) => `${n}:${d}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-rays="${dataRays}" data-angle="" data-droit="">${s}</svg>`;
};

// ─── Carte 4 : vrai ou faux ? (pool du modèle + deux lignes numériques) ────

const POOL_VF = [
    { l: 'Un angle plat mesure 180°.', v: true },
    { l: 'Un angle plein mesure 360°.', v: true },
    { l: 'Un angle droit mesure 90°.', v: true },
    { l: 'Deux angles opposés par le sommet ont la même mesure.', v: true },
    { l: 'Deux angles adjacents ont le même sommet et un côté commun.', v: true },
    { l: 'Un angle obtus mesure entre 90° et 180°.', v: true },
    { l: 'Un angle obtus mesure plus de 180°.', v: false },
    { l: 'Un angle plat mesure 360°.', v: false },
    { l: 'Deux angles adjacents sont toujours supplémentaires.', v: false },
    { l: 'Deux angles supplémentaires sont toujours adjacents.', v: false },
    { l: 'Deux angles opposés par le sommet sont supplémentaires.', v: false },
    { l: 'Un angle nul mesure 90°.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1072 : pool V/F déséquilibré');

let derniersServisVf1072 = [];

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Deux lignes NUMÉRIQUES recalculées : la nature d'un angle donné, deux angles supplémentaires ou non.
        const m = randomChoice([tirerAigu(), tirerObtus()]);
        const natureVraie = m < 90 ? 'aigu' : 'obtus';
        const natureDite = Math.random() < 0.5 ? natureVraie : (natureVraie === 'aigu' ? 'obtus' : 'aigu');
        const x = randInt(25, 155); const y = Math.random() < 0.5 ? 180 - x : 180 - x + randomChoice([-10, -5, 5, 10]);
        const numeriques = [
            { l: `Un angle de ${m}° est ${natureDite}.`, v: natureDite === natureVraie },
            { l: `Deux angles de ${x}° et ${y}° sont supplémentaires.`, v: x + y === 180 },
        ];
        // A8 (revue 2026-09-03) : la ligne numérique sur un obtus (mesure obtuse, ou
        // nature « obtus » affirmée) exclut les deux lignes de définition de l'obtus.
        const exclues = (m > 90 || natureDite === 'obtus')
            ? ['Un angle obtus mesure entre 90° et 180°.', 'Un angle obtus mesure plus de 180°.']
            : [];
        const dispo = POOL_VF.filter((it) => !derniersServisVf1072.includes(it.l) && !exclues.includes(it.l));
        const fixes = shuffle(dispo).slice(0, 2);
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisVf1072 = [...derniersServisVf1072, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1072 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNatureQcm,
    nature_cite_qcm_unique: genCiteQcm,
    relation_tableau_vf: genRelationVf,
    relation_oppose_qcm_unique: genOpposeQcm,
    mesure_saisie: genMesureSaisie,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', cite_qcm_unique: 'QCM cite', tableau_vf: 'Vrai/Faux', oppose_qcm_unique: 'QCM opposé', saisie: 'Saisie' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'relation', label: L_RELATION, formatsList: buildFormats('relation') },
    { id: 'mesure', label: L_MESURE, formatsList: buildFormats('mesure') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
